import { db, getMeta, META_KEYS } from "../../core/data/local/db";
import { uuidv7 } from "../../core/platform/id";
import type { DrawerAttempt, DrawerRecorder } from "./drawer-module";

/**
 * Persisting drawer attempts (T142, T144, FR-100, FR-101).
 *
 * THE RELEASE MUST NOT WAIT FOR THIS, AND THIS MUST NOT WAIT FOR THE NETWORK.
 * WebUSB is local I/O; a drawer opens during a total outage exactly as it does
 * online. So the record is written to IndexedDB and queued in the outbox, and
 * the kick never awaits a server (FR-101).
 *
 * The attempt is recorded BEFORE OR CONCURRENTLY WITH the kick, never after it
 * succeeds. If the write came last, a crash between the release and the write
 * would leave the drawer opened and nothing recording it — which is precisely
 * the event this whole feature exists to make impossible.
 */

const MODULE_ID = "peripherals";

export function createDrawerRecorder(shiftId: () => string | null): DrawerRecorder {
  return {
    async record(attempt): Promise<DrawerAttempt> {
      const full: DrawerAttempt = {
        ...attempt,
        id: uuidv7(),
        createdAt: new Date().toISOString(),
      };

      const opId = uuidv7();
      const terminalId = (await getMeta<string>(META_KEYS.terminalId)) ?? null;
      const shift = shiftId();

      // One transaction over both stores. A record that reached the module
      // store but not the outbox would be invisible to the back office
      // forever, and one that reached the outbox but not the store would
      // vanish from the terminal on reload.
      await db.transaction("rw", db.moduleRecords, db.outbox, async () => {
        await db.moduleRecords.put({
          opId,
          moduleId: MODULE_ID,
          kind: "drawer.event",
          shiftId: shift,
          record: full,
          createdAt: full.createdAt,
        });

        await db.outbox.put({
          opId,
          type: "drawer.event",
          payload: { ...full, shiftId: shift, terminalId },
          createdAt: full.createdAt,
          terminalId: terminalId ?? "",
          attempts: 0,
          state: "pending",
          lastError: null,
          nextAttemptAt: null,
        });
      });

      return full;
    },
  };
}

/** Failed electronic releases in a shift — the figure FR-107 puts on a Z reading. */
export async function countFailedReleases(shiftId: string): Promise<number> {
  const records = await db.moduleRecords
    .where("shiftId")
    .equals(shiftId)
    .toArray();

  return records.filter((row) => {
    const attempt = row.record as DrawerAttempt | undefined;
    return attempt?.method === "electronic" && attempt?.outcome === "failed";
  }).length;
}
